import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Alert, Modal } from 'react-bootstrap'
import { Link, useLocation, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faArrowUp,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons'
import {
  useQuickLinksData,
  useSaveQuickLinksMutation,
} from '../hooks/useQuickLinksData'
import useMessageStore from '../stores/message-store'
import styles from '../styles/quicklinks.module.css'

function cx(...classNames) {
  return classNames.filter(Boolean).join(' ')
}

function buildApplicationUrl(applicationId, segment) {
  return segment
    ? `/Client/Applications/${applicationId}/${segment}`
    : `/Client/Applications/${applicationId}`
}

function normalizePathname(pathname) {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return trimmed.toLowerCase()
}

function getDropPlacement(event) {
  const rect = event.currentTarget.getBoundingClientRect()
  const offsetX = event.clientX - rect.left
  const offsetY = event.clientY - rect.top
  const deltaX = offsetX - rect.width / 2
  const deltaY = offsetY - rect.height / 2

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    return deltaX >= 0 ? 'after' : 'before'
  }

  return deltaY >= 0 ? 'after' : 'before'
}

function useMediaQuery(query) {
  const getMatches = () =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false

  const [matches, setMatches] = useState(getMatches)

  useEffect(() => {
    const mediaQueryList = window.matchMedia(query)
    const handleChange = (event) => {
      setMatches(event.matches)
    }

    setMatches(mediaQueryList.matches)
    mediaQueryList.addEventListener('change', handleChange)

    return () => {
      mediaQueryList.removeEventListener('change', handleChange)
    }
  }, [query])

  return matches
}

function QuickLinksBody({
  title,
  copy,
  activeLinks,
  availableLinks,
  isEditing,
  isLoading,
  isSaving,
  loadError,
  saveError,
  onRetry,
  onEdit,
  onSave,
  onCancel,
  onRemove,
  onDropToActiveList,
  onDropToActivePill,
  onDragStart,
  onMoveLink,
  onAddAvailableLink,
  isLinkActive,
  activePathname = '',
  onScrollToTop,
  onCollapse,
  theme = 'desktop',
}) {
  const isMobile = theme === 'mobile'
  const mobileScrollerRef = useRef(null)
  const mobileLinkRefs = useRef(new Map())
  const hasPositionedInitialMobileLinkRef = useRef(false)
  const headingId = useId()
  const instructionsId = useId()
  const activeLabelId = useId()
  const availableLabelId = useId()
  const scrollMobileLinkIntoView = useCallback((linkId, behavior = 'smooth') => {
    const scroller = mobileScrollerRef.current
    const linkNode = mobileLinkRefs.current.get(linkId)

    if (!scroller || !linkNode) {
      return
    }

    const scrollerRect = scroller.getBoundingClientRect()
    const linkRect = linkNode.getBoundingClientRect()
    const centeredLeft =
      scroller.scrollLeft +
      linkRect.left -
      scrollerRect.left -
      (scroller.clientWidth - linkRect.width) / 2
    const maxLeft = Math.max(scroller.scrollWidth - scroller.clientWidth, 0)
    const nextLeft = Math.min(Math.max(centeredLeft, 0), maxLeft)
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    scroller.scrollTo({
      left: nextLeft,
      behavior: prefersReducedMotion ? 'auto' : behavior,
    })
  }, [])

  useEffect(() => {
    if (!isMobile || isEditing || !activePathname) {
      return
    }

    const activeLink = activeLinks.find(
      (link) => normalizePathname(link.url) === normalizePathname(activePathname),
    )

    if (!activeLink || typeof window === 'undefined') {
      return
    }

    const behavior = hasPositionedInitialMobileLinkRef.current ? 'smooth' : 'auto'

    const frameId = window.requestAnimationFrame(() => {
      scrollMobileLinkIntoView(activeLink.id, behavior)
      hasPositionedInitialMobileLinkRef.current = true
    })

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [
    activeLinks,
    activePathname,
    isEditing,
    isMobile,
    scrollMobileLinkIntoView,
  ])

  return (
    <section
      className={cx(
        styles.card,
        theme === 'mobile' ? styles.cardMobile : styles.cardDesktop,
      )}
      aria-labelledby={headingId}
    >
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>
            {isMobile ? 'Quick Links Dock' : 'Workspace'}
          </p>
          <h2 id={headingId} className={styles.title}>
            {title}
          </h2>
          <p className={styles.copy}>{copy}</p>
        </div>

        <div className={styles.headerActions}>
          {isEditing ? (
            <>
              <button
                type="button"
                className={cx(styles.button, styles.buttonGhost)}
                onClick={onCancel}
                disabled={isSaving}
                aria-label="Cancel quick links edits"
              >
                Cancel
              </button>
              <button
                type="button"
                className={cx(styles.button, styles.buttonPrimary)}
                onClick={onSave}
                disabled={isSaving}
                aria-label="Save quick links changes"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className={cx(styles.button, styles.buttonPrimary, styles.buttonEdit)}
              onClick={onEdit}
              disabled={isLoading}
              aria-expanded={isEditing}
              aria-describedby={instructionsId}
            >
              Edit
            </button>
          )}
        </div>
      </div>

      <p id={instructionsId} className={styles.srOnly}>
        {isMobile
          ? 'In edit mode, use the add, remove, up, and down controls to manage quick links.'
          : 'In edit mode, you can drag and drop links or use the move earlier and move later buttons for keyboard-accessible reordering.'}
      </p>

      {loadError && (
        <Alert variant="danger" className={styles.feedbackAlert}>
          <div className={styles.feedbackBody}>
            <div>
              <strong>Quick links failed to load.</strong>
              <div className={styles.feedbackText}>
                Refresh the quick links data and try again.
              </div>
            </div>
            <button
              type="button"
              className={cx(styles.button, styles.buttonPrimary)}
              onClick={onRetry}
            >
              Retry
            </button>
          </div>
        </Alert>
      )}

      {!loadError && saveError && (
        <Alert variant="danger" className={styles.feedbackAlert}>
          <strong>Quick links could not be saved.</strong>
          <div className={styles.feedbackText}>{saveError}</div>
        </Alert>
      )}

      {loadError ? null : (
        <>
      <div className={styles.zone}>
        <p id={activeLabelId} className={styles.label}>
          {isLoading
            ? 'Loading quick links'
            : isEditing
              ? 'Active links: drag to reorder, click a pill to remove'
              : 'Active links'}
        </p>

        {isLoading ? (
          <div className={styles.status} role="status" aria-live="polite">
            Loading placeholder data...
          </div>
        ) : isMobile && !isEditing ? (
          <div
            className={styles.marquee}
            ref={mobileScrollerRef}
            role="region"
            aria-labelledby={activeLabelId}
          >
            <div className={styles.marqueeTrack}>
              {activeLinks.map((link) => (
                <Link
                  key={link.id}
                  ref={(node) => {
                    if (node) {
                      mobileLinkRefs.current.set(link.id, node)
                      return
                    }

                    mobileLinkRefs.current.delete(link.id)
                  }}
                  to={link.url}
                  onClick={() => scrollMobileLinkIntoView(link.id)}
                  className={cx(
                    styles.pill,
                    styles.pillActive,
                    styles.pillMarquee,
                    isLinkActive(link.url) && styles.current,
                  )}
                  aria-current={isLinkActive(link.url) ? 'page' : undefined}
                >
                  <span>{link.label}</span>
                </Link>
              ))}
            </div>
          </div>
        ) : isMobile && isEditing ? (
          <div className={styles.mobileEditor}>
            <div
              className={cx(styles.pillGroup, styles.editing)}
              role="list"
              aria-labelledby={activeLabelId}
            >
              {activeLinks.map((link, index) => (
                <div key={link.id} className={styles.mobileItem} role="listitem">
                  <button
                    type="button"
                    className={cx(styles.pill, styles.pillActive)}
                    onClick={() => onRemove(link.id)}
                    aria-label={`Remove ${link.label} from quick links`}
                  >
                    <span>{link.label}</span>
                    <span className={styles.pillMeta}>Remove</span>
                  </button>
                  <div className={styles.mobileItemActions}>
                    <button
                      type="button"
                      className={cx(styles.button, styles.buttonGhost)}
                      onClick={() => onMoveLink(link.id, 'before')}
                      disabled={index === 0}
                      aria-label={`Move ${link.label} earlier`}
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className={cx(styles.button, styles.buttonGhost)}
                      onClick={() => onMoveLink(link.id, 'after')}
                      disabled={index === activeLinks.length - 1}
                      aria-label={`Move ${link.label} later`}
                    >
                      Down
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className={cx(styles.pillGroup, isEditing && styles.editing)}
            role="list"
            aria-labelledby={activeLabelId}
            onDragOver={(event) => {
              if (!isEditing) {
                return
              }

              event.preventDefault()
            }}
            onDrop={(event) => {
              if (!isEditing) {
                return
              }

              if (event.target !== event.currentTarget) {
                return
              }

              event.preventDefault()
              const draggedId = event.dataTransfer.getData('text/quick-link-id')

              if (draggedId) {
                onDropToActiveList(draggedId)
              }
            }}
          >
            {activeLinks.map((link) =>
              isEditing ? (
                <div key={link.id} className={styles.editableItem} role="listitem">
                  <button
                    type="button"
                    className={cx(
                      styles.pill,
                      styles.pillActive,
                      styles.draggable,
                    )}
                    draggable
                    onClick={() => onRemove(link.id)}
                    onDragStart={(event) => onDragStart(event, link.id)}
                    onDragOver={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      const draggedId = event.dataTransfer.getData(
                        'text/quick-link-id',
                      )
                      const placement = getDropPlacement(event)

                      if (draggedId) {
                        onDropToActivePill(draggedId, link.id, placement)
                      }
                    }}
                    aria-label={`Remove ${link.label} from quick links. Drag to reorder, or use the move buttons.`}
                    aria-describedby={instructionsId}
                  >
                    <span>{link.label}</span>
                    <span className={styles.pillMeta}>Remove</span>
                  </button>
                  <div className={styles.editActions}>
                    <button
                      type="button"
                      className={cx(styles.button, styles.buttonGhost, styles.smallButton)}
                      onClick={() => onMoveLink(link.id, 'before')}
                      aria-label={`Move ${link.label} earlier`}
                    >
                      Earlier
                    </button>
                    <button
                      type="button"
                      className={cx(styles.button, styles.buttonGhost, styles.smallButton)}
                      onClick={() => onMoveLink(link.id, 'after')}
                      aria-label={`Move ${link.label} later`}
                    >
                      Later
                    </button>
                  </div>
                </div>
              ) : (
                <Link
                  key={link.id}
                  to={link.url}
                  className={cx(
                    styles.pill,
                    styles.pillActive,
                    isLinkActive(link.url) && styles.current,
                  )}
                  aria-current={isLinkActive(link.url) ? 'page' : undefined}
                >
                  <span>{link.label}</span>
                </Link>
              ),
            )}
          </div>
        )}
      </div>

      {isEditing && !isLoading && (
        <div className={styles.zone}>
          <p id={availableLabelId} className={styles.label}>
            {isMobile
              ? 'Available links: tap to add'
              : 'Available links: drag into active links'}
          </p>
          <div
            className={cx(styles.pillGroup, styles.availableGroup)}
            role="list"
            aria-labelledby={availableLabelId}
          >
            {availableLinks.map((link) =>
              isMobile ? (
                <button
                  key={link.id}
                  type="button"
                  className={cx(styles.pill, styles.pillAvailable)}
                  onClick={() => onAddAvailableLink(link.id)}
                  aria-label={`Add ${link.label} to quick links`}
                >
                  <span>{link.label}</span>
                  <span className={styles.pillMeta}>Add</span>
                </button>
              ) : (
                <div key={link.id} className={styles.editableItem} role="listitem">
                  <button
                    type="button"
                    className={cx(
                      styles.pill,
                      styles.pillAvailable,
                      styles.draggable,
                    )}
                    draggable
                    onDragStart={(event) => onDragStart(event, link.id)}
                    onClick={() => onAddAvailableLink(link.id)}
                    aria-label={`Add ${link.label} to quick links. You can also drag it into the active links area.`}
                    aria-describedby={instructionsId}
                  >
                    <span>{link.label}</span>
                    <span className={styles.pillMeta}>Add or drag</span>
                  </button>
                </div>
              ),
            )}
          </div>
        </div>
      )}

      {isMobile && (
        <div className={styles.mobileFooter}>
          <button
            type="button"
            className={cx(styles.button, styles.buttonGhost, styles.mobileUtilityButton)}
            onClick={onScrollToTop}
          >
            <span>Top</span>
            <FontAwesomeIcon icon={faArrowUp} />
          </button>
          <button
            type="button"
            className={cx(styles.button, styles.buttonPrimary, styles.mobileUtilityButton)}
            onClick={onCollapse}
            disabled={isEditing}
          >
            <span>{isEditing ? 'Editing' : 'Hide Dock'}</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      )}
        </>
      )}
    </section>
  )
}

export default function QuickLinks() {
  const { applicationid = '' } = useParams()
  const applicationId = applicationid
  const location = useLocation()
  const isDesktopViewport = useMediaQuery('(min-width: 992px)')
  const { addToast } = useMessageStore((state) => ({ addToast: state.addToast }))
  const {
    availableLinks,
    activeLinks,
    isLoading,
    isError,
    refetch,
  } =
    useQuickLinksData(applicationId)
  const [draftActiveLinks, setDraftActiveLinks] = useState(activeLinks)
  const [isEditing, setIsEditing] = useState(false)
  const [isDesktopEditorOpen, setIsDesktopEditorOpen] = useState(false)
  const [mobilePortalRoot, setMobilePortalRoot] = useState(null)
  const [isMobileOpen, setIsMobileOpen] = useState(false)
  const [saveError, setSaveError] = useState('')
  const saveMutation = useSaveQuickLinksMutation(availableLinks)

  useEffect(() => {
    if (!isEditing) {
      setDraftActiveLinks(activeLinks)
    }
  }, [activeLinks, isEditing])

  useEffect(() => {
    setMobilePortalRoot(document.body)
  }, [])

  const workingActiveLinks = isEditing ? draftActiveLinks : activeLinks
  const availableById = new Set(workingActiveLinks.map((link) => link.id))
  const workingAvailableLinks = availableLinks
    .filter((link) => !availableById.has(link.id))
    .map((link) => ({
      id: link.id,
      label: link.name,
      url: buildApplicationUrl(applicationId, link.url),
      segment: link.url,
    }))

  const isLinkActive = (url) =>
    normalizePathname(location.pathname) === normalizePathname(url)

  const startEditing = () => {
    setDraftActiveLinks(activeLinks)
    setSaveError('')
    setIsEditing(true)

    if (isDesktopViewport) {
      setIsDesktopEditorOpen(true)
      return
    }

    setIsMobileOpen(true)
  }

  const cancelEditing = () => {
    setDraftActiveLinks(activeLinks)
    setSaveError('')
    setIsEditing(false)
    setIsDesktopEditorOpen(false)
  }

  const removeLink = (linkId) => {
    setDraftActiveLinks((current) => current.filter((link) => link.id !== linkId))
  }

  const moveLink = (draggedId, targetId = null, placement = 'before') => {
    const draggedLink =
      workingActiveLinks.find((link) => link.id === draggedId) ??
      workingAvailableLinks.find((link) => link.id === draggedId)

    if (!draggedLink) {
      return
    }

    setDraftActiveLinks((current) => {
      const withoutDragged = current.filter((link) => link.id !== draggedId)

      if (!targetId) {
        return [...withoutDragged, draggedLink]
      }

      const targetIndex = withoutDragged.findIndex((link) => link.id === targetId)

      if (targetIndex === -1) {
        return [...withoutDragged, draggedLink]
      }

      const insertIndex = placement === 'after' ? targetIndex + 1 : targetIndex
      withoutDragged.splice(insertIndex, 0, draggedLink)
      return withoutDragged
    })
  }

  const moveLinkByStep = (linkId, direction) => {
    setDraftActiveLinks((current) => {
      const currentIndex = current.findIndex((link) => link.id === linkId)

      if (currentIndex === -1) {
        return current
      }

      const nextIndex =
        direction === 'before' ? currentIndex - 1 : currentIndex + 1

      if (nextIndex < 0 || nextIndex >= current.length) {
        return current
      }

      const next = [...current]
      const [moved] = next.splice(currentIndex, 1)
      next.splice(nextIndex, 0, moved)
      return next
    })
  }

  const addAvailableLink = (linkId) => {
    moveLink(linkId)
  }

  const saveEditing = async () => {
    try {
      await saveMutation.mutateAsync(draftActiveLinks)
      setSaveError('')
      setIsEditing(false)
      setIsDesktopEditorOpen(false)
    } catch (error) {
      const message = error?.message || 'An unknown problem occured while saving quick links.'
      setSaveError(message)
      addToast({
        id: Math.random(),
        body: 'An error occured when saving quick links.',
        autoHide: true,
        errorFlag: true,
        timestamp: Date.now() - 1000,
      })
      console.log('QuickLinks save failed', {
        applicationId,
        error: message,
      })
    }
  }

  const handleDragStart = (event, linkId) => {
    event.dataTransfer.setData('text/quick-link-id', linkId)
    event.dataTransfer.effectAllowed = 'move'
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const mobileQuickLinks = (
    <div className={styles.mobileDock}>
      {isMobileOpen || isEditing ? (
        <div className={cx(styles.mobile, styles.visible)}>
          <QuickLinksBody
            title="Quick Links"
            copy="On smaller screens this mounts to the bottom edge. Display mode stays compact while edit mode exposes the full drag and save workflow."
            activeLinks={workingActiveLinks}
            availableLinks={workingAvailableLinks}
            isEditing={isEditing}
            isLoading={isLoading}
            isSaving={saveMutation.isPending}
            loadError={isError}
            saveError={saveError}
            onRetry={() => refetch()}
            onEdit={startEditing}
            onSave={saveEditing}
            onCancel={cancelEditing}
            onRemove={removeLink}
            onDropToActiveList={(draggedId) => moveLink(draggedId)}
            onDropToActivePill={(draggedId, targetId, placement) =>
              moveLink(draggedId, targetId, placement)
            }
            onDragStart={handleDragStart}
            onMoveLink={moveLinkByStep}
            onAddAvailableLink={addAvailableLink}
            isLinkActive={isLinkActive}
            activePathname={location.pathname}
            onScrollToTop={scrollToTop}
            onCollapse={() => setIsMobileOpen(false)}
            theme="mobile"
          />
        </div>
      ) : null}

      <button
        type="button"
        className={cx(styles.mobileToggle, isMobileOpen && styles.mobileToggleHidden)}
        onClick={() => setIsMobileOpen(true)}
        aria-label="Show quick links dock"
      >
        <span className={styles.mobileToggleText}>Quick Links</span>
        <FontAwesomeIcon icon={faChevronUp} />
      </button>
    </div>
  )

  return (
    <>
      <div className={styles.desktop}>
        <QuickLinksBody
          title="Quick Links"
          copy="Pinned application shortcuts for the current workspace."
          activeLinks={activeLinks}
          availableLinks={workingAvailableLinks}
          isEditing={false}
          isLoading={isLoading}
          isSaving={saveMutation.isPending}
          loadError={isError}
          saveError=""
          onRetry={() => refetch()}
          onEdit={startEditing}
          onSave={saveEditing}
          onCancel={cancelEditing}
          onRemove={removeLink}
          onDropToActiveList={(draggedId) => moveLink(draggedId)}
          onDropToActivePill={(draggedId, targetId, placement) =>
            moveLink(draggedId, targetId, placement)
          }
          onDragStart={handleDragStart}
          onMoveLink={moveLinkByStep}
          onAddAvailableLink={addAvailableLink}
          isLinkActive={isLinkActive}
          activePathname={location.pathname}
        />
      </div>

      <Modal
        show={isDesktopEditorOpen}
        onHide={cancelEditing}
        centered
        size="lg"
        dialogClassName={styles.desktopEditorDialog}
        contentClassName={styles.desktopEditorContent}
      >
        <Modal.Body className={styles.desktopEditorBody}>
          <QuickLinksBody
            title="Edit Quick Links"
            copy="Manage which links appear in the sidebar. Drag links to reorder them or add and remove items as needed."
            activeLinks={workingActiveLinks}
            availableLinks={workingAvailableLinks}
            isEditing={true}
            isLoading={isLoading}
            isSaving={saveMutation.isPending}
            loadError={isError}
            saveError={saveError}
            onRetry={() => refetch()}
            onEdit={startEditing}
            onSave={saveEditing}
            onCancel={cancelEditing}
            onRemove={removeLink}
            onDropToActiveList={(draggedId) => moveLink(draggedId)}
            onDropToActivePill={(draggedId, targetId, placement) =>
              moveLink(draggedId, targetId, placement)
            }
            onDragStart={handleDragStart}
            onMoveLink={moveLinkByStep}
            onAddAvailableLink={addAvailableLink}
            isLinkActive={isLinkActive}
            activePathname={location.pathname}
          />
        </Modal.Body>
      </Modal>

      {mobilePortalRoot ? createPortal(mobileQuickLinks, mobilePortalRoot) : null}
    </>
  )
}
